// Copyright (c) Ping. All rights reserved.
//
// PDF first-page rasterizer for document message tiles + the fullscreen
// reader. Uses pdf.js (`pdfjs-dist`) to render page 1 of a decrypted PDF
// blob to a PNG object URL — fed to `DocAttachmentCard`'s `previewUri` so
// the tile shows the rendered page (Figma 3412:4900) in both bubble and
// compact layouts. The full PDF object URL drives the in-app reader
// (`MediaPreviewScreen` doc kind → browser `<iframe>` viewer).
//
// Web-only (depends on `document.createElement('canvas')` + DOMMatrix).
// All failures degrade to `undefined` so the doc tile falls back to its
// icon-only compact layout — a render miss must never break the message.

import * as pdfjsLib from 'pdfjs-dist';
// Vite resolves the worker entry to a hashed URL; pdf.js needs it set
// before the first `getDocument` call.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

/** True for PDF blobs/messages — rasterized via pdf.js. */
export function isPdfMime(mime: string | undefined): boolean {
  if (mime === undefined) return false;
  const m = mime.toLowerCase();
  return m === 'application/pdf' || m.endsWith('/pdf');
}

// Text-like docs we can render as a "page of text" preview. Matched by
// MIME first, then by filename extension (servers often send a generic
// application/octet-stream for .txt/.md/etc.).
const TEXT_EXTS = new Set([
  'txt', 'text', 'md', 'markdown', 'csv', 'tsv', 'log', 'json', 'xml',
  'yml', 'yaml', 'ini', 'conf', 'cfg', 'env', 'js', 'ts', 'jsx', 'tsx',
  'py', 'rb', 'go', 'rs', 'java', 'c', 'h', 'cpp', 'cs', 'sh', 'sql',
  'html', 'css', 'svg',
]);

function extOf(name: string | undefined): string {
  if (name === undefined) return '';
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** True for text-ish docs we can render to a page-of-text thumbnail. */
export function isTextLike(mime: string | undefined, filename?: string): boolean {
  const m = (mime ?? '').toLowerCase();
  if (m.startsWith('text/')) return true;
  if (
    m === 'application/json' ||
    m === 'application/xml' ||
    m === 'application/javascript' ||
    m === 'application/x-yaml'
  ) {
    return true;
  }
  return TEXT_EXTS.has(extOf(filename));
}

/** True for Word `.docx` (OOXML) — previewed via a text extraction. */
export function isDocxLike(mime: string | undefined, filename?: string): boolean {
  const m = (mime ?? '').toLowerCase();
  if (
    m ===
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return true;
  }
  return extOf(filename) === 'docx';
}

/** True when we can produce a tile thumbnail for this doc (PDF, text, docx). */
export function canPreviewDoc(mime: string | undefined, filename?: string): boolean {
  return isPdfMime(mime) || isDocxLike(mime, filename) || isTextLike(mime, filename);
}

/**
 * Render the right kind of first-page preview for a doc blob: PDFs go
 * through pdf.js; `.docx` is text-extracted with `mammoth`; other
 * text-like docs are drawn directly as a "page of text". Returns
 * `undefined` for anything we can't preview (tile falls back to compact).
 */
export async function renderDocThumbnail(
  blob: Blob,
  mime: string | undefined,
  filename?: string,
): Promise<string | undefined> {
  if (isPdfMime(mime)) return renderPdfFirstPage(blob);
  if (isDocxLike(mime, filename)) return renderDocxFirstPage(blob);
  if (isTextLike(mime, filename)) return renderTextFirstPage(blob);
  return undefined;
}

/**
 * Extract the opening text of a `.docx` with `mammoth` and draw it as a
 * page-of-text thumbnail (same aesthetic as plain-text docs). Returns
 * `undefined` on any failure so the tile degrades to its compact layout.
 */
export async function renderDocxFirstPage(blob: Blob): Promise<string | undefined> {
  if (typeof document === 'undefined') return undefined;
  try {
    const mammoth = (await import('mammoth')).default;
    const arrayBuffer = await blob.arrayBuffer();
    const { value } = await mammoth.extractRawText({ arrayBuffer });
    if (typeof value !== 'string' || value.trim().length === 0) return undefined;
    return renderTextStringToPage(value);
  } catch {
    return undefined;
  }
}

/**
 * Render page 1 of `blob` to a PNG object URL (caller owns revocation).
 * `maxWidth` caps the raster width (the tile preview is ~292px wide; 600
 * keeps it crisp on HiDPI without blowing up memory). Returns `undefined`
 * on any failure (corrupt PDF, encrypted, render error).
 */
export async function renderPdfFirstPage(
  blob: Blob,
  opts: { maxWidth?: number } = {},
): Promise<string | undefined> {
  if (typeof document === 'undefined') return undefined;
  let pdf: pdfjsLib.PDFDocumentProxy | undefined;
  try {
    const data = new Uint8Array(await blob.arrayBuffer());
    pdf = await pdfjsLib.getDocument({ data }).promise;
    const page = await pdf.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const maxWidth = opts.maxWidth ?? 600;
    const scale = Math.min(2, Math.max(0.2, maxWidth / base.width));
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    if (ctx === null) return undefined;
    await page.render({ canvasContext: ctx, viewport }).promise;
    const out = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/png'),
    );
    if (out === null) return undefined;
    return URL.createObjectURL(out);
  } catch {
    return undefined;
  } finally {
    // Free the parsed document; the rendered PNG object URL is what we keep.
    try {
      await pdf?.destroy();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Draw the first ~portion of a text document onto a white "page" canvas
 * (Figma 3412:4900 page-thumbnail aesthetic) → PNG object URL. Caller owns
 * revocation. Returns `undefined` on failure.
 */
export async function renderTextFirstPage(
  blob: Blob,
  opts: { width?: number } = {},
): Promise<string | undefined> {
  if (typeof document === 'undefined') return undefined;
  try {
    // Cap how much we read — a thumbnail only needs the opening lines.
    const slice = blob.size > 16_384 ? blob.slice(0, 16_384) : blob;
    const text = await slice.text();
    return renderTextStringToPage(text, opts);
  } catch {
    return undefined;
  }
}

/**
 * Draw a text string onto a white "page" canvas → PNG object URL. Shared
 * by the plain-text and `.docx` (mammoth-extracted) preview paths.
 */
export async function renderTextStringToPage(
  text: string,
  opts: { width?: number } = {},
): Promise<string | undefined> {
  if (typeof document === 'undefined') return undefined;
  try {
    const width = opts.width ?? 480;
    const height = Math.round(width * 1.294); // ~ US-Letter portrait ratio
    const pad = 28;
    const fontSize = 15;
    const lineHeight = 21;
    const maxLines = Math.floor((height - pad * 2) / lineHeight);
    const maxCharsPerLine = Math.floor((width - pad * 2) / (fontSize * 0.6));

    // Soft-wrap the source into display lines, honouring hard newlines.
    const lines: string[] = [];
    for (const raw of text.split(/\r?\n/)) {
      if (lines.length >= maxLines) break;
      if (raw.length === 0) {
        lines.push('');
        continue;
      }
      for (let i = 0; i < raw.length && lines.length < maxLines; i += maxCharsPerLine) {
        lines.push(raw.slice(i, i + maxCharsPerLine));
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return undefined;
    // White page.
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    // Monospace body text, dark on white.
    ctx.fillStyle = '#1A1D24';
    ctx.font = `${fontSize}px ui-monospace, "SF Mono", Menlo, Consolas, monospace`;
    ctx.textBaseline = 'top';
    let y = pad;
    for (const line of lines) {
      ctx.fillText(line, pad, y);
      y += lineHeight;
    }

    const out = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/png'),
    );
    if (out === null) return undefined;
    return URL.createObjectURL(out);
  } catch {
    return undefined;
  }
}
